import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    CircularProgress,
    Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const heritageColors = {
    navy: '#1e3a5f',
    navyDark: '#152844',
    creamLight: '#faf8f3',
    white: '#ffffff',
    slate: '#64748b',
    slateLight: '#94a3b8',
    border: '#e2e8f0',
    borderLight: '#cbd5e1',
    red: '#dc2626',
    redDark: '#b91c1c',
    bgLight: '#f8fafc',
};

/**
 * CenterDialog - פופ-אפ מרכזי גמיש
 * 
 * @param {boolean} open - האם הדיאלוג פתוח
 * @param {function} onClose - פונקציה לסגירת הדיאלוג
 * @param {function} onConfirm - פונקציה לאישור (אופציונלי)
 * @param {string} title - כותרת הדיאלוג
 * @param {ReactNode} children - תוכן הדיאלוג
 * @param {boolean} isLoading - האם בטעינה
 * @param {boolean} confirmDisabled - האם כפתור האישור מושבת
 * @param {string} confirmText - טקסט כפתור האישור
 * @param {string} cancelText - טקסט כפתור הביטול
 * @param {string} confirmButtonColor - צבע כפתור האישור
 * @param {string} confirmButtonVariant - variant של כפתור האישור ('contained' / 'outlined')
 * @param {string} minWidth - רוחב מינימלי
 * @param {string} maxWidth - רוחב מקסימלי
 * @param {boolean} showCloseIcon - האם להציג X לסגירה
 * @param {boolean} hideActions - האם להסתיר את כפתורי הפעולה
 * @param {string} textAlign - יישור טקסט ('center' / 'right' / 'left')
 * @param {boolean} blueHeader - האם להציג כותרת עם רקע כחול
 */
export const CenterDialog = ({
    open,
    onClose,
    onConfirm,
    onCancel,
    title,
    children,
    isLoading = false,
    confirmDisabled = false,
    confirmText = "אישור",
    cancelText = "ביטול",
    confirmButtonColor,
    confirmButtonVariant = 'contained',
    minWidth = '320px',
    maxWidth = '500px',
    showCloseIcon = false,
    hideActions = false,
    textAlign = 'center',
    blueHeader = false
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '14px',
                    minWidth: minWidth,
                    maxWidth: maxWidth,
                    bgcolor: heritageColors.white,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.16), 0 4px 16px rgba(30,58,95,0.08)',
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    fontWeight: 600,
                    color: blueHeader ? heritageColors.white : heritageColors.navy,
                    fontSize: '1.125rem',
                    textAlign: textAlign,
                    pb: blueHeader ? 2 : 1,
                    pt: blueHeader ? 2 : 3,
                    px: 3,
                    display: 'flex',
                    justifyContent: showCloseIcon ? 'space-between' : 'center',
                    alignItems: 'center',
                    background: blueHeader ? 'linear-gradient(135deg, #112236 0%, #1e3a5f 100%)' : 'transparent',
                    borderRadius: blueHeader ? '14px 14px 0 0' : '0'
                }}
            >
                {showCloseIcon && (
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: blueHeader ? heritageColors.white : heritageColors.slate,
                            order: 1,
                            '&:hover': {
                                bgcolor: blueHeader ? 'rgba(255, 255, 255, 0.1)' : heritageColors.bgLight
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                <Box sx={{ flex: 1, textAlign: textAlign, order: 2 }}>
                    {title}
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent
                sx={{
                    textAlign: textAlign,
                    pt: 2,
                    pb: hideActions ? 3 : 2,
                    px: 3
                }}
            >
                {children}
            </DialogContent>

            {/* Actions */}
            {!hideActions && (
                <DialogActions
                    sx={{
                        justifyContent: 'center',
                        gap: 1.5,
                        pb: 2.5,
                        px: 3
                    }}
                >
                    {cancelText && (
                        <Button
                            onClick={onCancel || onClose}
                            variant="outlined"
                            disabled={isLoading}
                            sx={{
                                borderColor: heritageColors.borderLight,
                                color: heritageColors.slate,
                                '&:hover': {
                                    borderColor: heritageColors.slateLight,
                                    bgcolor: heritageColors.bgLight
                                },
                                borderRadius: '8px',
                                px: 3,
                                py: 1,
                                fontWeight: 600,
                                minWidth: '100px'
                            }}
                        >
                            {cancelText}
                        </Button>
                    )}

                    {onConfirm && (
                        <Button
                            onClick={onConfirm}
                            variant={confirmButtonVariant}
                            disabled={isLoading || confirmDisabled}
                            sx={{
                                bgcolor: confirmButtonColor || heritageColors.navy,
                                color: heritageColors.white,
                                boxShadow: '0 2px 8px rgba(30,58,95,0.2)',
                                transition: 'box-shadow 0.2s, background-color 0.2s',
                                '&:hover': {
                                    bgcolor: confirmButtonColor
                                        ? `${confirmButtonColor}dd`
                                        : heritageColors.navyDark,
                                    boxShadow: `0 4px 16px ${confirmButtonColor ? confirmButtonColor + '55' : 'rgba(30,58,95,0.35)'}`,
                                },
                                borderRadius: '8px',
                                px: 3,
                                py: 1,
                                fontWeight: 600,
                                minWidth: '100px',
                                ...(confirmButtonVariant === 'outlined' && {
                                    borderColor: confirmButtonColor || heritageColors.navy,
                                    color: confirmButtonColor || heritageColors.navy,
                                    bgcolor: 'transparent',
                                    '&:hover': {
                                        bgcolor: heritageColors.bgLight,
                                        borderColor: confirmButtonColor || heritageColors.navy
                                    }
                                })
                            }}
                        >
                            {isLoading ? (
                                <>
                                    <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />
                                    טוען...
                                </>
                            ) : (
                                confirmText
                            )}
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

// דוגמאות שימוש:

// 1. דיאלוג מחיקה פשוט
// <CenterDialog
//   open={deleteConfirmOpen}
//   onClose={handleDeleteCancel}
//   onConfirm={handleDeleteConfirm}
//   title="אישור מחיקה"
//   confirmText="מחק"
//   confirmButtonColor="#dc2626"
// >
//   <Typography sx={{ color: '#475569', fontSize: '0.95rem' }}>
//     האם אתה בטוח שברצונך למחוק?
//   </Typography>
// </CenterDialog>

// 2. דיאלוג עם תוכן מורכב
// <CenterDialog
//   open={editDialogOpen}
//   onClose={handleClose}
//   onConfirm={handleSave}
//   title="עריכת פרטים"
//   confirmText="שמור"
//   minWidth="400px"
//   maxWidth="600px"
//   textAlign="right"
//   showCloseIcon={true}
// >
//   <TextField fullWidth label="שם" />
//   <TextField fullWidth label="תיאור" />
// </CenterDialog>

// 3. דיאלוג הודעה בלבד (בלי כפתורים)
// <CenterDialog
//   open={messageOpen}
//   onClose={handleClose}
//   title="הודעה"
//   hideActions={true}
// >
//   <Typography>הפעולה בוצעה בהצלחה!</Typography>
// </CenterDialog>
